import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  StickyNote, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  Zap,
  Shield,
  Info,
  ChevronDown,
  FileUp,
  PenSquare
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import Tesseract from 'tesseract.js'
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const Alerts = () => {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const { getAlerts, deleteAlert, sendAlert, dismissAlert, addAlert, addAlertBoth, uploadImage } = useFirebase()
  const [menuOpen, setMenuOpen] = useState(false)
  const fileInputRef = useRef(null)
  const [confirmAlert, setConfirmAlert] = useState(null)

  useEffect(() => {
    loadAlerts()
  }, [])

  const loadAlerts = async () => {
    try {
      setLoading(true)
      const data = await getAlerts()
      setAlerts(data || [])
    } catch (error) {
      console.error('Error loading alerts:', error)
      toast.error('Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }

  const handleUploadPdfClick = () => {
    setMenuOpen(false)
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const extractPdfDetails = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

      // 1) Render first page as thumbnail image
      const page1 = await pdf.getPage(1)
      const viewport = page1.getViewport({ scale: 1.5 })
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = viewport.width
      canvas.height = viewport.height
      await page1.render({ canvasContext: context, viewport }).promise
      const imageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8))

      // 2) Heuristic title: choose bold and largest font near the top of page 1
      let title = ''
      const textContent1 = await page1.getTextContent()
      const items = (textContent1.items || []).filter(it => (it.str || '').trim())
      const topThreshold = viewport.height * 0.35
      const lineBuckets = new Map()
      items.forEach(it => {
        const t = it.transform || [1,0,0,1,0,0]
        const y = t[5] || 0
        const size = Math.max(Math.abs(t[0] || 0), Math.abs(t[3] || 0))
        const key = Math.round(y / 4) * 4
        const rec = lineBuckets.get(key) || { y: y, text: [], sizes: [] }
        rec.text.push(it.str)
        rec.sizes.push(size)
        rec.y = Math.max(rec.y, y)
        lineBuckets.set(key, rec)
      })
      const lines = Array.from(lineBuckets.values()).map(l => ({
        y: l.y,
        text: l.text.join(' ').replace(/\s+/g, ' ').trim(),
        avgSize: l.sizes.reduce((a,b)=>a+b,0)/(l.sizes.length||1)
      })).filter(l => l.text)
      lines.sort((a,b)=> b.avgSize - a.avgSize || b.y - a.y)
      const topCandidates = lines.filter(l => l.y >= viewport.height * 0.65)
      const pick = (topCandidates[0] || lines[0])
      if (pick && pick.text) {
        title = pick.text.replace(/_/g, ' ').slice(0,150).trim()
      }

      // Fallback: metadata Title
      if (!title) {
        try {
          const meta = await pdf.getMetadata()
          title = meta?.info?.Title || ''
        } catch {}
      }

      // 3) Long description: concatenate text of first 1-2 pages
      const pagesToRead = Math.min(3, pdf.numPages)
      let longText = ''
      for (let i = 1; i <= pagesToRead; i++) {
        const p = await pdf.getPage(i)
        const tc = await p.getTextContent()
        longText += ' ' + tc.items.map((it) => it.str || '').join(' ')
      }
      let description = longText.replace(/\s+/g, ' ').trim()

      // 4) OCR fallback if no text was extracted (e.g., scanned PDFs)
      if ((!title || title.length < 3) && (!description || description.length < 20)) {
        try {
          const blobUrl = URL.createObjectURL(imageBlob)
          const worker = await Tesseract.createWorker('eng')
          const { data } = await worker.recognize(blobUrl)
          await worker.terminate()
          URL.revokeObjectURL(blobUrl)
          const ocrText = (data?.text || '').replace(/\s+/g, ' ').trim()
          if (ocrText) {
            const lines = ocrText.split(/\n|\.|\!|\?/).map(s => s.trim()).filter(Boolean)
            const heading = lines.find(l => l.length > 5 && (/[A-Z]/.test(l) && (l === l.toUpperCase() || /\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+\b/.test(l)))) || lines[0]
            if (!title && heading) title = heading.slice(0,150)
            if (!description) {
              const para = ocrText.slice(0, 1500)
              if (para) {
                // prefer sentences after heading if possible
                const idx = ocrText.indexOf(heading)
                const rest = idx >= 0 ? ocrText.slice(idx + heading.length) : ocrText
                const desc = (rest || para).replace(/\s+/g, ' ').trim().slice(0, 1500)
                if (desc) {
                  description = desc
                }
              }
            }
          }
        } catch (ocrErr) {
          console.warn('OCR fallback failed:', ocrErr)
        }
      }

      // 5) Final fallbacks
      if (!title && description) {
        const firstSentence = description.split(/\.|\!|\?/)[0]
        title = firstSentence.replace(/_/g, ' ').slice(0,150).trim()
      }

      return { title, description, imageBlob }
    } catch (err) {
      // If pdfjs not installed or parsing fails, fallback
      console.warn('PDF parsing not available, falling back to filename:', err)
      return { title: '', description: '', imageBlob: null }
    }
  }

  const handlePdfSelected = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file')
      e.target.value = ''
      return
    }
    try {
      setLoading(true)
      // Try to extract details locally (graceful fallback if not available)
      const { title: parsedTitle, description: parsedDesc, imageBlob } = await extractPdfDetails(file)

      // Upload assets
      const pdfUrl = await uploadImage(file, 'notes-pdf')
      let imageUrl = ''
      if (imageBlob) {
        const imageFile = new File([imageBlob], `${(parsedTitle || 'note')}.jpg`, { type: 'image/jpeg' })
        imageUrl = await uploadImage(imageFile, 'notes-images')
      }

      const title = (parsedTitle && parsedTitle.trim()) || file.name.replace(/\.[^/.]+$/, '')
      const message = parsedDesc || ''

      await addAlertBoth({
        title,
        message,
        imageUrl,
        pdfUrl,
        type: 'notePdf',
        status: 'draft',
        priority: 'medium',
        targetUsers: 'all',
        createdDate: new Date().toISOString().split('T')[0],
        createdBy: 'admin@nursingexam.com'
      })
      toast.success('PDF uploaded and details saved')
      await loadAlerts()
    } catch (error) {
      console.error('Error uploading PDF note:', error)
      toast.error('Failed to upload PDF')
    } finally {
      setLoading(false)
      e.target.value = ''
    }
  }

  const handleDelete = (id) => {
    const a = alerts.find(x => x.id === id) || { id }
    setConfirmAlert(a)
  }

  const confirmDelete = async () => {
    if (!confirmAlert) return
    try {
      await deleteAlert(confirmAlert.id)
      await loadAlerts()
      toast.success('Alert deleted successfully')
    } catch (error) {
      console.error('Error deleting alert:', error)
      toast.error('Failed to delete alert')
    } finally {
      setConfirmAlert(null)
    }
  }

  const cancelDelete = () => setConfirmAlert(null)

  const handleSendAlert = async (id) => {
    if (window.confirm('Are you sure you want to send this alert to all users?')) {
      try {
        await sendAlert(id)
        setAlerts(alerts.map(alert => 
          alert.id === id 
            ? { ...alert, status: 'sent', sentAt: new Date().toISOString() }
            : alert
        ))
        toast.success('Notes sent successfully')
      } catch (error) {
        console.error('Error sending note:', error)
        toast.error('Failed to send note')
      }
    }
  }

  const handleDismissAlert = async (id) => {
    try {
      await dismissAlert(id)
      setAlerts(alerts.map(alert => 
        alert.id === id 
          ? { ...alert, status: 'dismissed' }
          : alert
      ))
      toast.success('Note dismissed')
    } catch (error) {
      console.error('Error dismissing note:', error)
      toast.error('Failed to dismiss note')
    }
  }

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.message?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const getTypeIcon = (type) => {
    switch (type) {
      case 'system':
        return <Shield className="h-4 w-4" />
      case 'maintenance':
        return <StickyNote className="h-4 w-4" />
      case 'security':
        return <Shield className="h-4 w-4" />
      case 'update':
        return <Bell className="h-4 w-4" />
      case 'emergency':
        return <Zap className="h-4 w-4" />
      default:
        return <Info className="h-4 w-4" />
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'system':
        return 'bg-blue-100 text-blue-800'
      case 'maintenance':
        return 'bg-orange-100 text-orange-800'
      case 'security':
        return 'bg-red-100 text-red-800'
      case 'update':
        return 'bg-green-100 text-green-800'
      case 'emergency':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-600" />
      case 'draft':
        return <Edit className="h-4 w-4 text-yellow-600" />
      case 'dismissed':
        return <XCircle className="h-4 w-4 text-gray-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800'
      case 'scheduled':
        return 'bg-blue-100 text-blue-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'dismissed':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDMY = (date) => {
    if (!date) return 'N/A'
    const d = new Date(date)
    if (isNaN(d)) return 'N/A'
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <StickyNote className="h-8 w-8 mr-3" />
              Notes
            </h1>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="btn-white inline-flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white text-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  <Link
                    to="/alerts/add"
                    className="block px-4 py-2 text-sm hover:bg-gray-100 inline-flex items-center"
                    onClick={() => setMenuOpen(false)}
                  >
                    <PenSquare className="h-4 w-4 mr-2" /> Create new note
                  </Link>
                  <button
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 inline-flex items-center"
                    onClick={handleUploadPdfClick}
                  >
                    <FileUp className="h-4 w-4 mr-2" /> Upload notes PDF
                  </button>
                </div>
              </div>
            )}
            <input
              type="file"
              accept="application/pdf"
              ref={fileInputRef}
              onChange={handlePdfSelected}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search notes by title or message..."
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Alerts List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Notes ({filteredAlerts.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <SkeletonTable rows={8} columns={5} />
          ) : filteredAlerts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Note Details</th>
                    <th>Date</th>
                    <th>Plan</th>
                    <th>Discount Plan</th>
                    <th>View Count</th>
                    <th>Add Subnode </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map((alert) => (
                    <tr key={alert.id} className="hover:bg-gray-50">
                      <td>
                        <div className="flex items-start space-x-3">
                          {alert.imageUrl ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                              <img 
                                src={alert.imageUrl} 
                                alt={alert.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                  e.target.nextSibling.style.display = 'flex'
                                }}
                              />
                              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                                <StickyNote className="h-5 w-5 text-red-600" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <StickyNote className="h-5 w-5 text-red-600" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 truncate">
                              {alert.title}
                            </h4>
                            <p className="text-sm text-gray-600 truncate max-w-xs">
                              {alert.message}
                            </p>
                            {null}
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-gray-600">
                        {(() => {
                          // Try different date fields and formats
                          if (alert.status === 'sent' && alert.sentAt) {
                            const sentDate = alert.sentAt?.toDate ? alert.sentAt.toDate() : new Date(alert.sentAt)
                            return formatDMY(sentDate)
                          }
                          if (alert.status === 'scheduled' && alert.scheduledAt) {
                            const scheduledDate = alert.scheduledAt?.toDate ? alert.scheduledAt.toDate() : new Date(alert.scheduledAt)
                            return formatDMY(scheduledDate)
                          }
                          if (alert.createdDate) {
                            // Use createdDate field which is a string date
                            return formatDMY(new Date(alert.createdDate))
                          }
                          if (alert.createdAt) {
                            // Handle Firestore timestamp
                            const createdDate = alert.createdAt?.toDate ? alert.createdAt.toDate() : new Date(alert.createdAt)
                            return formatDMY(createdDate)
                          }
                          return 'N/A'
                        })()}
                      </td>
                      <td className="text-sm text-gray-600">
                        <span className="font-medium">
                          {alert.plan || 'Free'}
                        </span>
                      </td>
                      <td className="text-sm text-gray-600">
                        <span className="font-medium">
                          {alert.discountPlan || '-'}
                        </span>
                      </td>
                      <td className="text-left">
                        <div className="flex items-center justify-center space-x-1">
                          <Eye className="h-4 w-4 text-indigo-500" />
                          <span className="font-medium text-indigo-600">{alert.viewCount || 0}</span>
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/alerts/${alert.id}/subnotes`}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200"
                          title="Add subnote"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Link>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/alerts/view/${alert.id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <Link
                            to={`/alerts/edit/${alert.id}`}
                            className="text-green-600 hover:text-green-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          {null}
                          <button
                            onClick={() => handleDelete(alert.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <StickyNote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notes found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first note'
                }
              </p>
              {!searchTerm && (
                <Link to="/alerts/add" className="btn-primary">
                  <Plus className="h-5 w-5 mr-2" />
                  Create First Note
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {confirmAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelDelete} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-2">Delete note?</div>
            <div className="text-sm text-gray-600 mb-6">
              This action cannot be undone. Remove
              {` ${confirmAlert?.title || confirmAlert?.id} `}
              from Alerts.
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={cancelDelete} className="btn-outline">Cancel</button>
              <button onClick={confirmDelete} className="btn-danger">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Alerts
